/**
 * TeamBuilder - Module for building teams and selecting AI players
 * 
 * This module handles the complex logic of building teams for battles,
 * including fetching available heroes, selecting AI players based on
 * battle frequency settings, creating random players when needed,
 * and assigning teams based on game mode.
 * 
 * @class TeamBuilder
 */
class TeamBuilder {
    constructor() {
        this.availableHeroes = [];
        this.userHeroMap = new Map(); // Maps user IDs to their heroes
    }

    /**
     * Main entry point for building teams
     * @param {Array} joinedPlayers - Array of manually joined players
     * @param {number} maxPlayers - Maximum number of players for the battle
     * @param {string} gameMode - Game mode (free-for-all, teams, etc.)
     * @param {number} teamCount - Number of teams (for team modes)
     * @returns {Promise<Object>} Object containing players array and teams object
     */
    async buildTeams(joinedPlayers, maxPlayers, gameMode, teamCount = 2) {
        try {
            console.log('🏗️ Building teams:', { joinedPlayers: joinedPlayers.length, maxPlayers, gameMode, teamCount });

            // Get user IDs to exclude from AI selection
            const excludeUserIds = joinedPlayers.map(p => p.userId).filter(id => id);
            
            // Fetch available heroes for AI selection
            await this.fetchAvailableHeroes(excludeUserIds);
            
            // Calculate how many AI players we need
            const neededAIPlayers = Math.max(0, maxPlayers - joinedPlayers.length);
            console.log(`📊 Need ${neededAIPlayers} AI players to reach ${maxPlayers} total`);

            // Select AI heroes
            const aiPlayers = await this.selectAIHeroes(neededAIPlayers);

            //set all aiPlayers platform to 'ai'
            aiPlayers.forEach(player => {
                player.platform = 'ai';
            });
            
            // Create random players if we still need more
            const stillNeeded = maxPlayers - joinedPlayers.length - aiPlayers.length;
            const randomPlayers = this.createRandomPlayers(stillNeeded);

            //set all randomPlayers platform to 'ai'
            randomPlayers.forEach(player => {
                player.platform = 'ai';
            });
            
            // Combine all players
            const allPlayers = [...joinedPlayers, ...aiPlayers, ...randomPlayers];
            
            // Assign teams based on game mode
            const teams = this.assignTeams(allPlayers, gameMode, teamCount);

            console.log('✅ Teams built successfully:', {
                totalPlayers: allPlayers.length,
                joinedPlayers: joinedPlayers.length,
                aiPlayers: aiPlayers.length,
                randomPlayers: randomPlayers.length,
                teams: Object.keys(teams).length
            });

            return {
                players: allPlayers,
                teams: teams,
                stats: {
                    joined: joinedPlayers.length,
                    ai: aiPlayers.length,
                    random: randomPlayers.length,
                    total: allPlayers.length
                }
            };

        } catch (error) {
            console.error('❌ Error building teams:', error);
            throw error;
        }
    }

    /**
     * Fetch available heroes for AI selection
     * @param {Array} excludeUserIds - Array of user IDs to exclude
     * @returns {Promise<void>}
     */
    async fetchAvailableHeroes(excludeUserIds) {
        try {
            const excludeParam = excludeUserIds.length > 0 ? excludeUserIds.join(',') : '';
            const url = `/api/heroes/for-battle?limit=100${excludeParam ? `&exclude_user_ids=${excludeParam}` : ''}`;
            
            console.log('🔍 Fetching available heroes:', url);
            
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch heroes: ${response.status} ${response.statusText}`);
            }
            
            const data = await response.json();
            if (!data.success) {
                throw new Error(`API error: ${data.error || 'Unknown error'}`);
            }
            
            this.availableHeroes = data.heroes || [];
            this.buildUserHeroMap();
            
            console.log(`📋 Found ${this.availableHeroes.length} available heroes for AI selection`);
            
        } catch (error) {
            console.error('❌ Error fetching available heroes:', error);
            this.availableHeroes = [];
            this.userHeroMap = new Map();
            throw error;
        }
    }

    /**
     * Build a map of user IDs to their heroes for efficient lookup
     */
    buildUserHeroMap() {
        this.userHeroMap.clear();
        
        this.availableHeroes.forEach(hero => {
            if (!this.userHeroMap.has(hero.user_id)) {
                this.userHeroMap.set(hero.user_id, []);
            }
            this.userHeroMap.get(hero.user_id).push(hero);
        });
        
        console.log(`🗺️ Built hero map for ${this.userHeroMap.size} users`);
    }

    /**
     * Select AI heroes based on battle frequency settings
     * @param {number} needed - Number of AI heroes needed
     * @returns {Promise<Array>} Array of AI player objects
     */
    async selectAIHeroes(needed) {
        if (needed <= 0) {
            return [];
        }

        console.log(`🤖 Selecting ${needed} AI heroes from ${this.availableHeroes.length} available`);

        const aiPlayers = [];
        const usedHeroIds = new Set();
        
        // Group heroes by user to respect battle frequency
        const userHeroGroups = Array.from(this.userHeroMap.entries());
        
        // Shuffle user groups to randomize selection
        this.shuffleArray(userHeroGroups);
        
        let attempts = 0;
        const maxAttempts = needed * 3; // Prevent infinite loops
        
        while (aiPlayers.length < needed && attempts < maxAttempts) {
            attempts++;
            
            // Try to select one hero from each user group
            for (const [userId, userHeroes] of userHeroGroups) {
                if (aiPlayers.length >= needed) break;
                
                // Filter out already used heroes
                const availableHeroes = userHeroes.filter(hero => !usedHeroIds.has(hero.id));
                
                if (availableHeroes.length === 0) continue;
                
                // Select a random hero from this user's available heroes
                const selectedHero = availableHeroes[Math.floor(Math.random() * availableHeroes.length)];
                
                // Check battle frequency setting
                if (this.shouldSelectHero(selectedHero)) {
                    //console.log('[TeamBuilder] Selecting AI hero:', selectedHero);
                    const aiPlayer = this.createAIPlayerFromHero(selectedHero);
                    console.log('[TeamBuilder] Created AI player:', aiPlayer);
                    aiPlayers.push(aiPlayer);
                    usedHeroIds.add(selectedHero.id);
                    
                    //console.log(`✅ Selected AI hero: ${aiPlayer.name} (User: ${userId})`);
                    //console.log('[TeamBuilder] selectedHero:', aiPlayer);
                }
            }
        }
        
        console.log(`🎯 Selected ${aiPlayers.length} AI heroes (attempts: ${attempts})`);
        return aiPlayers;
    }

    /**
     * Check if a hero should be selected based on battle frequency
     * @param {Object} hero - Hero object
     * @returns {boolean} Whether to select this hero
     */
    shouldSelectHero(hero) {
        // If no battle_frequency setting, default to 50% chance
        if (!hero.battle_frequency) {
            return Math.random() < 0.5;
        }
        
        // Convert battle_frequency to probability (0-1)
        const frequency = parseFloat(hero.battle_frequency);
        if (isNaN(frequency)) {
            return Math.random() < 0.5;
        }
        
        // Clamp frequency between 0 and 1
        const probability = Math.max(0, Math.min(1, frequency));
        
        return Math.random() < probability;
    }

    /**
     * Create an AI player object from a hero
     * @param {Object} hero - Hero object from database
     * @returns {Object} AI player object
     */
    createAIPlayerFromHero(hero) {
        return {
            id: `ai_${hero.id}`,
            name: hero.name || `AI Hero ${hero.id}`,
            //wallet: `ai_wallet_${hero.id}`,
            userId: hero.user_id,
            heroId: hero.id,
            isAI: true,
            isJoined: false,
            teamId: null, // Will be assigned later
            hero: {
                id: hero.id,
                name: hero.name,
                image_url: hero.image_url && hero.image_url !== '⚔️' ? hero.image_url : null, // Use image_url field with fallback
                level: hero.level || 1,
                /*
                //formulas for calculating the stats
                health = 50 + Math.floor(endurance / 2) + Math.floor(strength / 2) + secondary_spent_health;
                stamina = 50 + endurance + Math.floor(quickness / 2) + secondary_spent_stamina; //TODO check this formula
                mana = 50 + Math.floor(focus / 2) + Math.floor(self / 2) + secondary_spent_mana; //TODO check this formula
                speed = Math.floor(coordination / 2) + Math.floor(quickness / 2);
                defense = Math.floor((quickness + coordination) / 3);
                attack = Math.floor((strength + coordination) / 2);
                */
                stats: {
                    strength: hero.strength || 10,
                    endurance: hero.endurance || 10,
                    coordination: hero.coordination || 10,
                    quickness: hero.quickness || 10,
                    focus: hero.focus || 10,
                    self: hero.self || 10,
                    health: 250 + Math.floor(hero.endurance / 2) + Math.floor(hero.strength / 2) + hero.secondary_spent_health || 100,
                    stamina: 50 + hero.endurance + Math.floor(hero.quickness / 2) + hero.secondary_spent_stamina || 100, //TODO check this formula
                    mana: 50 + Math.floor(hero.focus / 2) + Math.floor(hero.self / 2) + hero.secondary_spent_mana || 100, //TODO check this formula
                    speed: Math.floor(hero.quickness),
                    defense: Math.floor((hero.quickness + hero.coordination) / 3), //TODO check this formula
                    attack: Math.floor((hero.strength + hero.coordination) / 2), //TODO check this formula
                    secondary_spent_health: hero.secondary_spent_health || 0,
                    secondary_spent_stamina: hero.secondary_spent_stamina || 0,
                    secondary_spent_mana: hero.secondary_spent_mana || 0,
                },
                battle_frequency: hero.battle_frequency,
                inventory: hero.inventory || [], // Include hero's inventory
                strategies: {
                    aggression: hero.strategy_aggression ?? 50,
                    looting: hero.strategy_looting ?? 50,
                    positioning: hero.strategy_positioning ?? 50,
                    melee_ranged: hero.strategy_melee_ranged ?? 50,
                    item_use: hero.strategy_item_use ?? 50
                }
            },
            spawnPosition: null // Will be set during spawning
        };
    }

    /**
     * Create random players when not enough heroes are available
     * @param {number} needed - Number of random players needed
     * @returns {Array} Array of random player objects
     */
    createRandomPlayers(needed) {
        if (needed <= 0) {
            return [];
        }

        console.log(`🎲 Creating ${needed} random players`);

        // Arrays for generating random names
        const prefixes = [
            'Elite', 'Cool', 'Big', 'Swift', 'Wild', 'Bold', 'Brave',
            'Shadow', 'Storm', 'Fire', 'Ice', 'Steel', 'Dark',
            'Epic', 'Cosmic', 'Primal',
            'Raging', 'Frozen', 'Toxic', 'Ruby', 'Jade', 'Pearl',
            'Iron', 'Bronze', 'Uru',
            'Ghost', 'Soul', 'Wraith', 'Lich',
            'Royal', 'Noble', 'Hero',
            'Master', 'Grand', 'Prime', 'Chief', 'Fast', 'Quick',
            'Smart', 'Sharp', 'Wise'
        ];

        const animalNames = [
            'Wolf', 'Bear', 'Eagle', 'Lion', 'Tiger', 'Fox', 'Hawk', 'Panther', 'Falcon', 'Lynx',
            'Raven', 'Coyote', 'Owl', 'Jaguar', 'Cheetah', 'Viper', 'Cobra', 'Python', 'Shark', 'Orca',
            'Dragon', 'Phoenix', 'Griffin', 'Basilisk', 'Hydra', 'Kraken', 'Leviathan', 'Cerberus', 'Minotaur', 'Centaur',
            'Rhino', 'Elephant', 'Gorilla', 'Buffalo', 'Bison', 'Moose', 'Elk', 'Deer', 'Antelope', 'Gazelle',
            'Puma', 'Cougar', 'Bobcat', 'Wolverine', 'Badger', 'Otter', 'Beaver', 'Marten', 'Fisher', 'Sable',
            'Stag', 'Ram', 'Bull', 'Stallion', 'Colt', 'Mare', 'Filly', 'Buck', 'Doe', 'Fawn',
            'Crane', 'Heron', 'Egret', 'Stork', 'Pelican', 'Albatross', 'Condor', 'Vulture', 'Buzzard', 'Kite',
            'Serpent', 'Asp', 'Adder', 'Mamba', 'Rattler', 'Boa', 'Anaconda', 'Gila', 'Gecko', 'Iguana',
            'Mantis', 'Scorpion', 'Tarantula', 'Widow', 'Recluse', 'Hornet', 'Wasp', 'Beetle', 'Dragonfly', 'Cicada',
            'Salamander', 'Newt', 'Toad', 'Frog', 'Turtle', 'Tortoise', 'Lizard', 'Chameleon', 'Monitor', 'Skink'
        ];

        const randomPlayers = [];
        
        for (let i = 0; i < needed; i++) {
            // Generate a random name with prefix, animal, and number
            const randomPrefix = prefixes[Math.floor(Math.random() * prefixes.length)];
            const randomAnimal = animalNames[Math.floor(Math.random() * animalNames.length)];
            const randomNumber = Math.floor(Math.random() * 999) + 1; // 1-999
            
            // 70% chance to include prefix, 30% chance to include number
            let randomAnimalName;
            if (Math.random() < 0.7) {
                // Include prefix
                if (Math.random() < 0.3) {
                    // Include both prefix and number
                    randomAnimalName = `${randomPrefix}${randomAnimal}${randomNumber}`;
                } else {
                    // Just prefix and animal
                    randomAnimalName = `${randomPrefix}${randomAnimal}`;
                }
            } else {
                // No prefix, but maybe number
                if (Math.random() < 0.4) {
                    // Just animal and number
                    randomAnimalName = `${randomAnimal}${randomNumber}`;
                } else {
                    // Just animal name
                    randomAnimalName = randomAnimal;
                }
            }
            
            const randomPlayer = {
                id: `random_${Date.now()}_${i}`,
                name: randomAnimalName,
                wallet: `random_wallet_${Date.now()}_${i}`,
                userId: null,
                heroId: null,
                isAI: true,
                isRandom: true, // Flag to identify random heroes
                isJoined: false,
                teamId: null,
                hero: {
                    id: null,
                    name: randomAnimalName,
                    image_url: null, // Random heroes will use fallback textures
                    level: Math.floor(Math.random() * 10) + 1,
                    stats: {
                        health: Math.floor(Math.random() * 50) + 80,
                        attack: Math.floor(Math.random() * 30) + 40,
                        defense: Math.floor(Math.random() * 20) + 25,
                        speed: Math.floor(Math.random() * 25) + 30
                    },
                    battle_frequency: 1.0,
                    inventory: [], // Random heroes start with no inventory
                    strategies: {
                        aggression: Math.floor(Math.random() * 100),
                        looting: Math.floor(Math.random() * 100),
                        positioning: Math.floor(Math.random() * 100),
                        melee_ranged: Math.floor(Math.random() * 100),
                        item_use: Math.floor(Math.random() * 100)
                    }
                },
                spawnPosition: null
            };
            
            randomPlayers.push(randomPlayer);
        }
        
        console.log(`✅ Created ${randomPlayers.length} random players with animal names`);
        return randomPlayers;
    }

    /**
     * Assign teams to players based on game mode
     * @param {Array} players - Array of all players
     * @param {string} gameMode - Game mode
     * @param {number} teamCount - Number of teams
     * @returns {Object} Teams object with team IDs as keys
     */
    assignTeams(players, gameMode, teamCount) {
        console.log(`🏆 Assigning teams for ${gameMode} mode with ${teamCount} teams`);
        
        const teams = {};
        
        // Initialize team objects
        for (let i = 0; i < teamCount; i++) {
            teams[i] = {
                id: i,
                name: `Team ${i + 1}`,
                players: [],
                color: this.getTeamColor(i)
            };
        }
        
        if (gameMode === 'free-for-all') {
            // In free-for-all, everyone is on their own team
            players.forEach((player, index) => {
                player.teamId = index;
                teams[index] = {
                    id: index,
                    name: player.name,
                    players: [player],
                    color: this.getTeamColor(index)
                };
            });
        } else if (gameMode === 'teams') {
            // Distribute players evenly across teams
            const shuffledPlayers = [...players];
            this.shuffleArray(shuffledPlayers);
            
            shuffledPlayers.forEach((player, index) => {
                const teamId = index % teamCount;
                player.teamId = teamId;
                teams[teamId].players.push(player);
            });
        } else {
            // Default to free-for-all for unknown modes
            console.warn(`⚠️ Unknown game mode '${gameMode}', defaulting to free-for-all`);
            players.forEach((player, index) => {
                player.teamId = index;
                teams[index] = {
                    id: index,
                    name: player.name,
                    players: [player],
                    color: this.getTeamColor(index)
                };
            });
        }
        
        console.log('🎯 Team assignment complete:', Object.keys(teams).map(teamId => 
            `Team ${teamId}: ${teams[teamId].players.length} players`
        ));
        
        return teams;
    }

    /**
     * Get a color for a team based on its ID
     * @param {number} teamId - Team ID
     * @returns {string} Color hex code
     */
    getTeamColor(teamId) {
        const colors = [
            '#FF6B6B', // Red
            '#4ECDC4', // Teal
            '#45B7D1', // Blue
            '#96CEB4', // Green
            '#FFEAA7', // Yellow
            '#DDA0DD', // Plum
            '#98D8C8', // Mint
            '#F7DC6F'  // Gold
        ];
        
        return colors[teamId % colors.length];
    }

    /**
     * Shuffle an array in place using Fisher-Yates algorithm
     * @param {Array} array - Array to shuffle
     */
    shuffleArray(array) {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
    }

    /**
     * Get debug information about the team builder state
     * @returns {Object} Debug information
     */
    getDebugInfo() {
        return {
            availableHeroes: this.availableHeroes.length,
            userHeroMap: this.userHeroMap.size,
            userHeroCounts: Array.from(this.userHeroMap.entries()).map(([userId, heroes]) => ({
                userId,
                heroCount: heroes.length
            }))
        };
    }
}

// Export singleton instance
const teamBuilder = new TeamBuilder();
export default teamBuilder;
